import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Activity, DollarSign } from 'lucide-react';
import { useMarketData } from '../contexts/MarketDataContext';
import { TradingMode } from '../App';
import { useSimulatedOrderBook } from '../hooks/useSimulatedOrderBook';

interface OrderBookEntry {
  price: number;
  amount: number;
  total: number;
}

interface OrderBookProps {
  selectedPair: string;
  orderBook?: {  // Made optional since we're not passing it anymore
    bids: OrderBookEntry[];
    asks: OrderBookEntry[];
    lastUpdateId: number;
  };
  tradingMode: TradingMode;
}

const OrderBook: React.FC<OrderBookProps> = ({
  selectedPair,
  orderBook,
  tradingMode
}) => {
  const { getPriceBySymbol } = useMarketData();
  const [displayMode, setDisplayMode] = useState<'both' | 'bids' | 'asks'>('both');
  const [precision, setPrecision] = useState(2);
  const [grouping, setGrouping] = useState(0.5);
  const currentPrice = getPriceBySymbol(selectedPair);

  const hasRealOrderBook = Boolean(orderBook && orderBook.bids.length > 0 && orderBook.asks.length > 0);
  const simulatedOrderBook = useSimulatedOrderBook(selectedPair, hasRealOrderBook ? 0 : currentPrice, 15);
  const displayOrderBook = hasRealOrderBook && orderBook ? orderBook : simulatedOrderBook;
  const quoteAvailable = Number.isFinite(currentPrice) && currentPrice > 0;


  // Format price based on precision
  const formatPrice = (price: number) => {
    return price.toFixed(precision);
  };

  // Format amount
  const formatAmount = (amount: number) => {
    return amount.toFixed(4);
  };

  // Format total
  const formatTotal = (total: number) => {
    return total.toFixed(4);
  };

  // Calculate depth percentage for visualization
  const calculateDepthPercentage = (total: number, maxTotal: number) => {
    return (total / maxTotal) * 100;
  };

  // Get max total for visualization scaling
  const maxBidTotal = displayOrderBook.bids.length > 0
    ? displayOrderBook.bids[displayOrderBook.bids.length - 1].total
    : 0;
    
  const maxAskTotal = displayOrderBook.asks.length > 0
    ? displayOrderBook.asks[displayOrderBook.asks.length - 1].total
    : 0;

  return (
    <div className="h-full flex flex-col" style={{ background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)' }} translate="no">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: 'rgba(0, 0, 0, 0.08)' }}>
        <h3 className="font-bold" style={{ color: '#000000' }}>Order Book</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setDisplayMode('both')}
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
            style={{
              background: displayMode === 'both' ? 'linear-gradient(135deg, rgba(217, 18, 34, 0.15), rgba(217, 18, 34, 0.05))' : 'transparent',
              color: displayMode === 'both' ? '#d91222' : '#64748b',
              border: displayMode === 'both' ? '1px solid rgba(217, 18, 34, 0.2)' : '1px solid transparent'
            }}
          >
            Both
          </button>
          <button
            onClick={() => setDisplayMode('bids')}
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
            style={{
              background: displayMode === 'bids' ? 'linear-gradient(135deg, rgba(22, 163, 74, 0.15), rgba(22, 163, 74, 0.05))' : 'transparent',
              color: displayMode === 'bids' ? '#16a34a' : '#64748b',
              border: displayMode === 'bids' ? '1px solid rgba(22, 163, 74, 0.2)' : '1px solid transparent'
            }}
          >
            Bids
          </button>
          <button
            onClick={() => setDisplayMode('asks')}
            className="text-xs px-3 py-1.5 rounded-lg font-medium transition-all"
            style={{
              background: displayMode === 'asks' ? 'linear-gradient(135deg, rgba(220, 38, 38, 0.15), rgba(220, 38, 38, 0.05))' : 'transparent',
              color: displayMode === 'asks' ? '#dc2626' : '#64748b',
              border: displayMode === 'asks' ? '1px solid rgba(220, 38, 38, 0.2)' : '1px solid transparent'
            }}
          >
            Asks
          </button>
        </div>
      </div>

      {/* Order Book Content */}
      <div className="flex-1 overflow-auto" translate="no">
        {/* Column Headers */}
        <div className="grid grid-cols-3 text-xs font-semibold p-3 border-b sticky top-0"
          style={{
            color: '#64748b',
            borderColor: 'rgba(0, 0, 0, 0.08)',
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
          }}>
          <div className="flex items-center gap-1">
            <DollarSign size={12} />
            Price ({tradingMode === 'cfd' ? 'USD' : 'USDT'})
          </div>
          <div className="text-right flex items-center justify-end gap-1">
            <Activity size={12} />
            Amount
          </div>
          <div className="text-right flex items-center justify-end gap-1">
            <TrendingUp size={12} />
            Total
          </div>
        </div>

        {/* Asks (Sell Orders) - Only show if displayMode is 'both' or 'asks' */}
        {(displayMode === 'both' || displayMode === 'asks') && (
          <div className="border-b" style={{ borderColor: 'rgba(0, 0, 0, 0.08)' }}>
            {displayOrderBook.asks.slice().reverse().map((ask, index) => (
              <div
                key={`ask-${index}`}
                className="grid grid-cols-3 text-xs p-2.5 relative transition-all hover:shadow-sm"
                style={{
                  background: 'rgba(255, 255, 255, 0.5)',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.04)'
                }}
              >
                <div className="font-mono font-semibold flex items-center gap-1" style={{ color: '#dc2626' }}>
                  <TrendingDown size={10} />
                  {formatPrice(ask.price)}
                </div>
                <div className="text-right font-mono" style={{ color: '#64748b' }}>{formatAmount(ask.amount)}</div>
                <div className="text-right font-mono" style={{ color: '#64748b' }}>{formatTotal(ask.total)}</div>
                <div
                  className="absolute right-0 top-0 bottom-0 rounded-r"
                  style={{
                    width: `${calculateDepthPercentage(ask.total, maxAskTotal)}%`,
                    background: 'linear-gradient(90deg, transparent, rgba(220, 38, 38, 0.08))'
                  }}
                ></div>
              </div>
            ))}
          </div>
        )}

        {/* Current Price */}
        {displayMode === 'both' && (
          <div
            className="grid grid-cols-3 text-sm p-3 border-b font-bold"
            style={{
              background: 'linear-gradient(135deg, rgba(217, 18, 34, 0.1), rgba(217, 18, 34, 0.05))',
              borderColor: 'rgba(217, 18, 34, 0.2)'
            }}
          >
            <div className="font-mono flex items-center gap-2" style={{ color: '#d91222' }}>
              <Activity size={14} />
              {quoteAvailable ? currentPrice.toFixed(precision) : '--'}
            </div>
            <div className="text-right text-xs flex items-center justify-end" style={{ color: '#64748b' }}>Current Price</div>
            <div className="text-right"></div>
          </div>
        )}

        {/* Bids (Buy Orders) - Only show if displayMode is 'both' or 'bids' */}
        {(displayMode === 'both' || displayMode === 'bids') && (
          <div>
            {displayOrderBook.bids.map((bid, index) => (
              <div
                key={`bid-${index}`}
                className="grid grid-cols-3 text-xs p-2.5 relative transition-all hover:shadow-sm"
                style={{
                  background: 'rgba(255, 255, 255, 0.5)',
                  borderBottom: '1px solid rgba(0, 0, 0, 0.04)'
                }}
              >
                <div className="font-mono font-semibold flex items-center gap-1" style={{ color: '#16a34a' }}>
                  <TrendingUp size={10} />
                  {formatPrice(bid.price)}
                </div>
                <div className="text-right font-mono" style={{ color: '#64748b' }}>{formatAmount(bid.amount)}</div>
                <div className="text-right font-mono" style={{ color: '#64748b' }}>{formatTotal(bid.total)}</div>
                <div
                  className="absolute right-0 top-0 bottom-0 rounded-r"
                  style={{
                    width: `${calculateDepthPercentage(bid.total, maxBidTotal)}%`,
                    background: 'linear-gradient(90deg, transparent, rgba(22, 163, 74, 0.08))'
                  }}
                ></div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div
        className="p-4 border-t text-xs flex justify-between items-center"
        style={{
          borderColor: 'rgba(0, 0, 0, 0.08)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
        }}
      >
        <div className="flex items-center gap-2">
          <span className="font-semibold" style={{ color: '#64748b' }}>Precision:</span>
          <select
            value={precision}
            onChange={(e) => setPrecision(parseInt(e.target.value))}
            className="px-3 py-1.5 rounded-lg border font-medium transition-all"
            style={{
              background: '#ffffff',
              color: '#000000',
              borderColor: 'rgba(0, 0, 0, 0.1)'
            }}
          >
            <option value="0">0</option>
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="3">3</option>
            <option value="4">4</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="font-semibold" style={{ color: '#64748b' }}>Group:</span>
          <select
            value={grouping}
            onChange={(e) => setGrouping(parseFloat(e.target.value))}
            className="px-3 py-1.5 rounded-lg border font-medium transition-all"
            style={{
              background: '#ffffff',
              color: '#000000',
              borderColor: 'rgba(0, 0, 0, 0.1)'
            }}
          >
            <option value="0.1">0.1</option>
            <option value="0.5">0.5</option>
            <option value="1">1.0</option>
            <option value="5">5.0</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default OrderBook;
